import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, ProgressBar } from "react-bootstrap";
import InputMask from "react-input-mask";
import CreatableSelect from "react-select/creatable";
import api from "../../../../Utils/api";
import OccurrenceNatureSelect from "../../../../components/OccurrenceNatureSelect";
import { getLocalityCatalog, putLocalityCatalog } from "../../../../offline/draftStore";


function FormHeader({ boletim, setBoletim, municipio: municipioInicial, onFieldChange, coordinateErrors = {} }) {
  const municipio = boletim.municipio || municipioInicial;
  const [localidades, setLocalidades] = useState([]);
  const [localidadeBusca, setLocalidadeBusca] = useState(boletim.localidadeNome || boletim.bairro || "");
  const [localidadeQuery, setLocalidadeQuery] = useState("");
  const [localidadesOffline, setLocalidadesOffline] = useState(false);
  const [localidadeError, setLocalidadeError] = useState("");
  useEffect(() => {
    if (municipio && !boletim.municipio) setBoletim((prev) => ({ ...prev, municipio: prev.municipio || municipio }));
  }, [municipio, boletim.municipio, setBoletim]);

  useEffect(() => {
    setLocalidadeBusca(boletim.localidadeNome || boletim.bairro || "");
  }, [boletim.localidadeId, boletim.localidadeNome, boletim.bairro]);

  useEffect(() => {
    let active = true;
    if (!municipio) { setLocalidades([]); return () => { active = false; }; }
    const query = localidadeQuery.trim();
    const timer = setTimeout(() => {
      api.get("/localidades", { params: { municipio, ...(query ? { q: query } : {}), limit: 100 } })
        .then(async ({ data }) => {
          const items = (data.items || []).filter((item) => item?._id && item.nome && item.tipo && item.zona);
          if (!query) await putLocalityCatalog(municipio, items).catch(() => undefined);
          if (active) { setLocalidades(items); setLocalidadesOffline(false); }
        })
        .catch(async () => {
          const cached = await getLocalityCatalog(municipio).catch(() => null);
          if (active) { setLocalidades(cached || []); setLocalidadesOffline(true); }
        });
    }, query ? 250 : 0);
    return () => { active = false; clearTimeout(timer); };
  }, [municipio, localidadeQuery]);

  const handleChange = (field, value) => {
    onFieldChange?.(field);
    setBoletim((prev) => ({ ...prev, [field]: value }));
  };

  const selecionarLocalidade = (item) => {
    setLocalidadeError("");
    onFieldChange?.();
    setBoletim((prev) => ({ ...prev, bairro: item.nome, localidadeId: item._id, localidadeNome: item.nome, localidadeTipo: item.tipo, zona: item.zona }));
    setLocalidadeBusca(item.nome);
  };
  const alterarFiltroTerritorial = (field, value) => {
    onFieldChange?.(field);
    setBoletim((prev) => ({ ...prev, [field]: value, localidadeId: "", localidadeNome: "" }));
  };
  const digitarLocalidade = (value) => {
    onFieldChange?.("bairro");
    setBoletim((prev) => ({
      ...prev,
      bairro: value,
      localidadeId: "",
      localidadeNome: "",
      localidadeTipo: "",
    }));
  };
  const localidadesVisiveis = localidades.filter((item) => (
    item.ativo && item.nome && item.tipo && item.zona &&
    (!boletim.zona || item.zona === boletim.zona) &&
    (!boletim.localidadeTipo || item.tipo === boletim.localidadeTipo)
  )).slice(0, 100);
  const localityOptions = localidadesVisiveis.map((item) => ({
    value: String(item._id),
    label: item.nome,
    aliases: item.aliases || [],
    item,
  }));
  const selectedLocality = boletim.localidadeId
    ? localityOptions.find((option) => option.value === String(boletim.localidadeId)) || {
      value: String(boletim.localidadeId),
      label: boletim.localidadeNome || boletim.bairro,
    }
    : localidadeBusca ? { value: "__unlisted__", label: localidadeBusca, isNew: true } : null;
  const normalizeLocalityQuery = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR").trim();
  const cadastrarLocalidadeDigitada = async (name) => {
    const nome = String(name || "").trim();
    if (!nome) return;
    setLocalidadeBusca(nome);
    if (!boletim.localidadeTipo) {
      setLocalidadeError("Selecione o tipo de localidade antes de adicioná-la ao catálogo.");
      digitarLocalidade(nome);
      return;
    }
    setLocalidadeError("");
    try {
      const { data: item } = await api.post("/localidades/registrar-no-boletim", {
        nome,
        municipio,
        tipo: boletim.localidadeTipo,
        zona: boletim.zona || "NAO_INFORMADA",
      });
      setLocalidades((current) => [item, ...current.filter((locality) => String(locality._id) !== String(item._id))]);
      selecionarLocalidade(item);
    } catch (error) {
      onFieldChange?.("bairro");
      setBoletim((prev) => ({ ...prev, bairro: nome, localidadeId: "", localidadeNome: "" }));
      setLocalidadeError(error.response?.data?.message || "Não foi possível adicionar ao catálogo. Verifique a conexão e tente novamente.");
    }
  };

  return (
    <Container fluid className="bo-form-root">
      <ProgressBar now={20} className="bo-progress" />

      <Form>
        <section className="bo-section">
          <h5 className="bo-section-title">Ocorrência</h5>

          <div className="bo-field">
            <label htmlFor="boletim-numero">Número do B.O.</label>
            <input
              id="boletim-numero"
              type="text"
              aria-invalid={Boolean(coordinateErrors.numero)}
              className={coordinateErrors.numero ? 'is-invalid' : ''}
              required
              value={boletim.numero || ""}
              placeholder="Informe o número"
              onChange={(e) => handleChange("numero", e.target.value)}
            />
            {coordinateErrors.numero && <div className="invalid-feedback d-block">{coordinateErrors.numero}</div>}
          </div>

          <Row className="g-2">
            <Col xs={6}>
              <div className="bo-field">
                <label htmlFor="boletim-data">Data</label>
                <InputMask
                  id="boletim-data"
                  inputMode="numeric"
                  aria-invalid={Boolean(coordinateErrors.data)}
                  className={coordinateErrors.data ? 'is-invalid' : ''}
                  mask="99/99/9999"
                  placeholder="DD/MM/AAAA"
                  required
                  value={boletim.data || ""}
                  onChange={(e) => handleChange("data", e.target.value)}
                />
                {coordinateErrors.data && <div className="invalid-feedback d-block">{coordinateErrors.data}</div>}
              </div>
            </Col>

            <Col xs={6}>
              <div className="bo-field">
                <label htmlFor="boletim-horario">Hora</label>
                <input
                  id="boletim-horario"
                  aria-invalid={Boolean(coordinateErrors.horario)}
                  className={coordinateErrors.horario ? 'is-invalid' : ''}
                  type="time"
                  value={boletim.horario || ""}
                  onChange={(e) => handleChange("horario", e.target.value)}
                />
                {coordinateErrors.horario && <div className="invalid-feedback d-block">{coordinateErrors.horario}</div>}
              </div>
            </Col>
          </Row>

          <div className="bo-field">
            <label htmlFor="natureza-ocorrencia">Natureza da Ocorrência</label>
            <OccurrenceNatureSelect boletim={boletim} setBoletim={setBoletim} onFieldChange={onFieldChange} />
            {coordinateErrors.natureza && <div className="invalid-feedback d-block">{coordinateErrors.natureza}</div>}
          </div>
        </section>

        {/* ================= LOCAL ================= */}
        <section className="bo-section">
          <h5 className="bo-section-title">Local do Fato</h5>

          <Row className="g-2">
            <Col xs={6}>
              <div className="bo-field">
                <label htmlFor="latitude">Latitude</label>
                <input
                  id="latitude"
                  required
                  type="text"
                  inputMode="decimal"
                  className={coordinateErrors.latitude ? "is-invalid" : ""}
                  aria-invalid={Boolean(coordinateErrors.latitude)}
                  aria-describedby={coordinateErrors.latitude ? "latitude-error" : undefined}
                  value={boletim.latitude ?? ""}
                  placeholder="Latitude"
                  onChange={(e) => handleChange("latitude", e.target.value)}
                />
                <button type="button" className="btn btn-link btn-sm p-0" aria-label="Alternar sinal da latitude"
                  onClick={() => handleChange('latitude', String(boletim.latitude ?? '').startsWith('-') ? String(boletim.latitude).slice(1) : `-${String(boletim.latitude ?? '').replace(/^\+/, '')}`)}>±</button>
                {coordinateErrors.latitude && (
                  <div id="latitude-error" className="invalid-feedback d-block">
                    {coordinateErrors.latitude}
                  </div>
                )}
              </div>
            </Col>

            <Col xs={6}>
              <div className="bo-field">
                <label htmlFor="longitude">Longitude</label>
                <input
                  id="longitude"
                  required
                  type="text"
                  inputMode="decimal"
                  className={coordinateErrors.longitude ? "is-invalid" : ""}
                  aria-invalid={Boolean(coordinateErrors.longitude)}
                  aria-describedby={coordinateErrors.longitude ? "longitude-error" : undefined}
                  value={boletim.longitude ?? ""}
                  placeholder="Longitude"
                  onChange={(e) => handleChange("longitude", e.target.value)}
                />
                <button type="button" className="btn btn-link btn-sm p-0" aria-label="Alternar sinal da longitude"
                  onClick={() => handleChange('longitude', String(boletim.longitude ?? '').startsWith('-') ? String(boletim.longitude).slice(1) : `-${String(boletim.longitude ?? '').replace(/^\+/, '')}`)}>±</button>
                {coordinateErrors.longitude && (
                  <div id="longitude-error" className="invalid-feedback d-block">
                    {coordinateErrors.longitude}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <div className="bo-field">
            <label>Endereço</label>
            <input
              value={boletim.endereco || ""}
              placeholder="Logradouro"
              onChange={(e) => handleChange("endereco", e.target.value)}
            />
          </div>

          <Row className="g-2">
            <Col xs={6}><div className="bo-field"><label>Número</label><input value={boletim.numeroEndereco || ""} placeholder="Nº" onChange={(e) => handleChange("numeroEndereco", e.target.value)} /></div></Col>
            <Col xs={6}><div className="bo-field"><label htmlFor="territorio-zona">Zona</label><select id="territorio-zona" value={boletim.zona || ""} onChange={(e) => alterarFiltroTerritorial("zona", e.target.value)}><option value="">Não informada</option><option value="URBANA">Urbana</option><option value="RURAL">Rural</option><option value="NAO_INFORMADA">Não informada</option></select></div></Col>
          </Row>

          <Row className="g-2">
            <Col xs={12} md={5}><div className="bo-field"><label htmlFor="territorio-tipo">Tipo de localidade</label><select id="territorio-tipo" value={boletim.localidadeTipo || ""} onChange={(e) => alterarFiltroTerritorial("localidadeTipo", e.target.value)}><option value="">Todos / não informado</option>{[["BAIRRO", "Bairro"], ["POVOADO", "Povoado"], ["LOCALIDADE_RURAL", "Localidade rural"], ["VILA", "Vila"], ["DISTRITO", "Distrito"], ["ASSENTAMENTO", "Assentamento"], ["COMUNIDADE", "Comunidade"], ["OUTRO", "Outro"]].map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div></Col>
            <Col xs={12} md={7}>
              <div className="bo-field"><label htmlFor="territorio-localidade">Localidade (bairro, povoado ou zona rural)</label>
                <CreatableSelect
                  inputId="territorio-localidade"
                  aria-label="Localidade (bairro, povoado ou zona rural)"
                  value={selectedLocality}
                  options={localityOptions}
                  onChange={(option) => {
                    if (!option) { setLocalidadeBusca(""); setLocalidadeError(""); digitarLocalidade(""); return; }
                    if (option.item) { selecionarLocalidade(option.item); return; }
                    cadastrarLocalidadeDigitada(option.label);
                  }}
                  onCreateOption={cadastrarLocalidadeDigitada}
                  onInputChange={(value, meta) => {
                    if (meta.action === "input-change") setLocalidadeQuery(value);
                    return value;
                  }}
                  formatCreateLabel={(name) => name}
                  isClearable
                  isSearchable
                  placeholder="Pesquise ou digite uma localidade"
                  noOptionsMessage={({ inputValue }) => inputValue ? "Nenhuma localidade cadastrada compatível." : "Nenhuma localidade disponível para os filtros."}
                  filterOption={(candidate, input) => {
                    const search = normalizeLocalityQuery(input);
                    return !search || normalizeLocalityQuery(`${candidate.label} ${(candidate.data.aliases || []).join(" ")}`).includes(search);
                  }}
                  styles={{ control: (base) => ({ ...base, minHeight: 42, borderColor: "#ced4da" }), menu: (base) => ({ ...base, zIndex: 20 }) }}
                />
                {localidadesOffline && <small className="text-muted">Catálogo local em uso; localidades novas ficam sem vínculo oficial até a sincronização.</small>}
                {!boletim.localidadeId && <small className="text-muted">Se não encontrar, digite o nome, selecione o tipo de localidade e confirme para adicioná-la ao catálogo.</small>}
                {!boletim.localidadeId && boletim.bairro && <button type="button" className="btn btn-link btn-sm p-0 d-block" onClick={() => cadastrarLocalidadeDigitada(boletim.bairro)}>Adicionar “{boletim.bairro}” deste boletim ao catálogo</button>}
                {localidadeError && <small className="text-danger d-block" role="alert">{localidadeError}</small>}
              </div>
            </Col>
          </Row>

          <div className="bo-field">
            <label>Município</label>
            <input value={boletim.municipio || ""} disabled />
            {coordinateErrors.municipio && <div className="invalid-feedback d-block">{coordinateErrors.municipio}</div>}
          </div>

          <div className="bo-field">
            <label>Ponto de Referência</label>
            <input
              value={boletim.referencia || ""}
              placeholder="Referência"
              onChange={(e) => handleChange("referencia", e.target.value)}
            />
          </div>
        </section>
      </Form>
    </Container>
  );
}

export default FormHeader;
